// src/pages/LoginPage.jsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  Button
} from '@mui/material';
import logo from '../assets/logo-sao-pedro.png';
import Lottie from 'lottie-react';
import busAnimation from '../assets/bus.json';

export default function LoginPage() {
  const navigate = useNavigate();

  // URL do backend - usa variável de ambiente ou fallback para produção
  const API_BASE = process.env.REACT_APP_API_BASE ?? '';
  const API_KEY = process.env.REACT_APP_API_KEY;
  
  console.log('  LoginPage - API_BASE (env):', process.env.REACT_APP_API_BASE);
  console.log('🔍 LoginPage - API_BASE (final):', API_BASE);

  // estados do formulário
  const [usuario, setUsuario] = React.useState('');
  const [senha, setSenha]     = React.useState('');
  const [setor, setSetor]     = React.useState('Financeiro');

  const [isLoading, setIsLoading] = React.useState(false);
  const [errorMsg, setErrorMsg]   = React.useState('');

  const SETORES = ['Financeiro','Tráfego','Compras','TI','Manutenção','Marketing','Jurídico'];

  // helper para fazer login (reaproveitado pelos dois botões)
  async function doLogin({ username, password, setor }) {
    const headers = { 'Content-Type': 'application/json' };
    if (API_KEY) headers['x-api-key'] = API_KEY; // ← mudança: minúsculo
    
    const payload = { username, password, setor };
    console.log('🚀 Enviando login:', { API_BASE, headers, payload });

    const res = await fetch(`${API_BASE}/api/login`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify(payload)
    });

    // evita quebrar se o servidor responder HTML de erro
    const ct = res.headers.get('content-type') || '';
    if (!ct.includes('application/json')) {
      const txt = await res.text();
      throw new Error(`Falha no servidor (${res.status}). ${txt.slice(0,80)}…`);
    }

    const data = await res.json();
    if (!res.ok || !data.ok) {
      const err = data?.error || 'LOGIN_FAILED';
      throw new Error(err);
    }

    // guarda dados não sensíveis para personalizar a UI
    sessionStorage.setItem('user', JSON.stringify(data.user));
    return data.user;
  }

  // botão "Entrar" -> login e vai para /menu
  async function handleLogin(event) {
    event.preventDefault();
    setErrorMsg('');

    if (!usuario.trim() || !senha.trim()) {
      setErrorMsg('Por favor, preencha usuário e senha.');
      return;
    }

    setIsLoading(true);
    try {
      await doLogin({ username: usuario, password: senha, setor });
      navigate('/menu');
    } catch (err) {
      let msg = 'Usuário ou senha inválidos.';
      if (String(err.message) === 'SECTOR_NOT_ALLOWED') msg = 'Setor não permitido para este usuário.';
      if (String(err.message) === 'MISSING_CREDENTIALS') msg = 'Credenciais ausentes.';
      if (String(err.message).startsWith('Falha no servidor')) msg = 'Falha ao comunicar com o servidor.';
      setErrorMsg(msg);
    } finally {
      setIsLoading(false);
    }
  }

  // botão "CREDENCIAIS" -> login e vai direto para /admin/usuarios (somente Joziel / quem pode gerenciar)
  async function handleLoginAdmin(event) {
    event.preventDefault();
    setErrorMsg('');

    if (!usuario.trim() || !senha.trim()) {
      setErrorMsg('Por favor, preencha usuário e senha.');
      return;
    }

    setIsLoading(true);
    try {
      const user = await doLogin({ username: usuario, password: senha, setor });
      const allowed = user.role === 'MASTER' && (user.username === 'Joziel' || user.canManageUsers);
      if (!allowed) {
        alert('Apenas o Joziel tem acesso à administração de usuários.');
        return;
      }
      navigate('/admin/usuarios');
    } catch (err) {
      let msg = 'Não foi possível entrar. Verifique usuário/senha/setor.';
      if (String(err.message).startsWith('Falha no servidor')) msg = 'Falha ao comunicar com o servidor.';
      setErrorMsg(msg);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        bgcolor: 'background.default',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        px: 2,
        pb: 16 // Espaço extra para o footer
      }}
    >
      {/* Logo removida do canto superior */}

      {/* Card do formulário */}
      <Paper
        elevation={3}
        sx={{ 
          p: 4, 
          width: '100%', 
          maxWidth: 400, 
          bgcolor: 'background.paper', 
          borderRadius: 2,
          position: 'relative',
          zIndex: 1
        }}
      >
        <form onSubmit={handleLogin} noValidate>
          <Box
            component="img"
            src={logo}
            alt="Logo São Pedro"
            sx={{
              width: { xs: 200, sm: 220, md: 240 },
              height: 'auto',
              mb: 3,
              mx: 'auto',
              display: 'block',
              transition: 'all 0.3s ease',
              filter: theme => 
                theme.palette.mode === 'light' 
                  ? 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))'
                  : 'brightness(1.2) contrast(1.3) saturate(1.2) drop-shadow(0 0 20px rgba(33, 150, 243, 0.5)) drop-shadow(0 4px 12px rgba(0,0,0,0.4))',
              '&:hover': {
                transform: 'scale(1.02)',
                filter: theme => 
                  theme.palette.mode === 'light' 
                    ? 'drop-shadow(0 6px 12px rgba(0,0,0,0.15))'
                    : 'brightness(1.25) contrast(1.35) saturate(1.25) drop-shadow(0 0 25px rgba(33, 150, 243, 0.6)) drop-shadow(0 6px 16px rgba(0,0,0,0.5))'
              }
            }}
          />

          <TextField
            fullWidth
            label="Usuário"
            margin="normal"
            variant="outlined"
            value={usuario}
            onChange={e => setUsuario(e.target.value)}
            autoComplete="username"
            inputProps={{ 'aria-label': 'Usuário' }}
            error={Boolean(errorMsg)}
          />

          <TextField
            select
            label="Setor"
            value={setor}
            onChange={e => setSetor(e.target.value)}
            SelectProps={{ native: true }}
            margin="normal"
            fullWidth
          >
            {SETORES.map(name => (
              <option key={name} value={name}>{name}</option>
            ))}
          </TextField>

          <TextField
            fullWidth
            label="Senha"
            type="password"
            margin="normal"
            variant="outlined"
            value={senha}
            onChange={e => setSenha(e.target.value)}
            autoComplete="current-password"
            inputProps={{ 'aria-label': 'Senha' }}
            error={Boolean(errorMsg)}
            helperText={errorMsg || ' '}
          />

          {/* Entrar -> /menu */}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            sx={{ mt: 1.5, fontFamily: 'Roboto, sans-serif', fontWeight: 'bold', letterSpacing: '0.5px' }}
            disabled={isLoading}
          >
            {isLoading ? 'Entrando...' : 'Entrar'}
          </Button>

          {/* Credenciais -> login com os campos + /admin/usuarios */}
          <Button
            type="button"
            fullWidth
            variant="contained"
            onClick={handleLoginAdmin}
            disabled={isLoading}
            sx={{
              mt: 1,
              bgcolor: '#9242FF',
              color: '#FFFFFF',
              fontWeight: 'bold',
              letterSpacing: '0.5px',
              '&:hover': { bgcolor: '#9242FF' }
            }}
          >
            CREDENCIAIS
          </Button>
        </form>
      </Paper>

      {/* Rodapé com animação de ônibus */}
      <Box
        component="footer"
        sx={{
          position: 'fixed',
          bottom: 0, left: 0, width: '100%', height: 120,
          bgcolor: 'background.default', display: 'flex',
          justifyContent: 'center', alignItems: 'center', overflow: 'hidden',
          zIndex: 0 // Garantir que fique atrás do card
        }}
      >
        <Box sx={{ width: 300, height: 120 }}>
          <Lottie animationData={busAnimation} loop autoplay style={{ width: '100%', height: '100%' }} />
        </Box>
      </Box>
    </Box>
  );
}